import React, { useEffect, useState } from "react";
import { Page, Text, View, Document, StyleSheet, Font, Image } from "@react-pdf/renderer";
import { formatPostalCode, formatToJapaneseDate } from "./common";

// ✅ 日本語フォントを登録
Font.register({
  family: "NotoSansJP",
  src: "/fonts/NotoSansJP-Regular.ttf", // 🔹 ローカルの OTF フォントを参照
});

const styles = StyleSheet.create({
  page: { padding: "50 30", fontSize: 10, fontFamily: "NotoSansJP", position: "relative" }, // ✅ 日本語フォント適用
  section: { marginBottom: 10 },
  header: { fontSize: 15, padding: "5 40" },
  table: { display: "table", width: "100%", border: "1px solid black", borderBottom: "none", marginTop: 10 },
  row: { flexDirection: "row", borderBottom: "1px solid black" },
  cell: { textAlign: "center", borderRight: "1px solid black", padding: "5 1", overflow: "hidden" }, // ✅ 日本語フォント適用

  rowBetween: { flexDirection: "row", justifyContent: "space-between", marginBottom: 5 },
  leftText: { textAlign: "left", flexGrow: 1 },
  rightText: { textAlign: "right", flexGrow: 1, alignItems: "flex-end" },
  roundBorder: { textAlign: "center", border: "1px solid black", borderRadius: "10px", padding: "5 10" },
  rowCenter: { flexDirection: "row", justifyContent: "center", marginTop: 5 },
  name: { borderBottom: "1px solid black", marginTop: 5, padding: "0 10" },
  total: { border: "2px solid black", marginTop: 40, padding: "5 10", display: "inline" },
  annai: { border: "1px dashed black", padding: "5 30", margin: "10 30", borderRadius: "10px" },
});

export const loadImageAsBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob); // ← base64に変換される
  });
};


// 請求書コンポーネント
const InvoicePDF = ({ invoiceData }) => {
  const [stamp, setStamp] = useState(null);

  useEffect(() => {
    loadImageAsBase64(`${process.env.REACT_APP_DIR}/kakuin.png`).then(setStamp);
  }, []);

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.rowBetween}>
          <View style={styles.leftText}>
            <Text>〒{invoiceData.postCode ? invoiceData.postCode : ""}</Text>
            <Text>{invoiceData.address ?? ""}</Text>
            <View style={{ ...styles.rowCenter, ...styles.name }}>
              <Text style={{ fontSize: 15, marginTop: 10 }}>{invoiceData.name ?? ""}　</Text>
              <Text style={{ marginTop: 15 }}>様</Text>
            </View>
            <Text style={{ marginTop: 50 }}>下記の通り御請求申し上げます</Text>
            <Text style={styles.total}>{`御請求金額　￥　　${invoiceData.totalAmount != null ? invoiceData.totalAmount.toLocaleString() : ""}`}</Text>

          </View>
          <View style={styles.rightText}>
            <Text style={styles.header}>請　求　書</Text>
            <Text style={{ margin: "5 50 10" }}>{formatToJapaneseDate(new Date(invoiceData.issueDate))}</Text>
            <Text >ひたちなか市三反田字走り下り145-1</Text>
            <Text style={{ fontSize: 12 }}>有限会社　丸　越　産　業</Text>
            <Text >代表取締役　打　越　太　教</Text>
            <Text style={{ fontSize: 12 }}>TEL　029（279）0408</Text>
            <Text style={{ fontSize: 12 }}>FAX　029（229）3040</Text>
            <Text >登録番号　T9050002008961</Text>
            <Image
              src={stamp}
              style={{
                width: 60,
                height: 60,
                position: "absolute",
                top: 60,
                right: 0,
              }}
            />
            <View style={{ ...styles.roundBorder, ...styles.rowCenter }}>
              <View style={{ textAlign: "left" }}>
                <Text>振込先：</Text>
              </View>
              <View style={{ textAlign: "right" }}>
                <Text>茨城県信用組合　勝田中央支店</Text>
                <Text >普通　9005529</Text>
                <Text >常陽銀行　ひたちなか支店</Text>
                <Text >普通　6256212</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={{ alignItems: "flex-end" }}>
          <View style={styles.rowCenter}>
            <View style={{ textAlign: "left" }}>
              <Text>※</Text>
            </View>
            <View style={{ textAlign: "right" }}>
              <Text>お振込み手数料はお客様ご負担でお願いいたします。</Text>
              <Text>本状と行き違いにて入金済の場合はご容赦ください。</Text>
            </View>
          </View>
        </View>


        <View style={styles.table}>
          <View style={[styles.row]}>
            <Text style={{ ...styles.cell, flex: 1 }}>月　日</Text>
            <Text style={{ ...styles.cell, flex: 4 }}>品　目</Text>
            <Text style={{ ...styles.cell, flex: 1 }}>数　量</Text>
            <Text style={{ ...styles.cell, flex: 1.5 }}>単　価</Text>
            <Text style={{ ...styles.cell, flex: 1.5 }}>金　額</Text>
            <Text style={{ ...styles.cell, borderRight: "none", flex: 2 }}>摘　要</Text>
          </View>
          {invoiceData.items.map((item, index) => (
            <View key={index} style={styles.row}>
              <Text wrap style={{ ...styles.cell, flex: 1 }}>{item.date ? `${(new Date(item.date)).getMonth() + 1} / ${(new Date(item.date)).getDate()}` : ""}</Text>
              <Text wrap style={{ ...styles.cell, flex: 4, textAlign: "left" }}>{item.name}</Text>
              <Text wrap style={{ ...styles.cell, flex: 1 }}>{item.quantity != null ? item.quantity.toLocaleString() : ""}</Text>
              <Text wrap style={{ ...styles.cell, flex: 1.5 }}>{item.price != null ? item.price.toLocaleString() : ""}</Text>
              <Text wrap style={{ ...styles.cell, flex: 1.5, textAlign: "right" }}>{(item.price != null && item.quantity != null) ? (item.price * item.quantity).toLocaleString() : ""}</Text>
              <Text wrap style={{ ...styles.cell, borderRight: "none", flex: 2 }}>{item.summary}</Text>
            </View>
          ))}
          <View style={styles.row}>
            <Text style={{ ...styles.cell, flex: 7.5, padding: "5 5.5" }}>合　　計</Text>
            <Text style={{ ...styles.cell, flex: 1.5, textAlign: "right" }}>{invoiceData.totalAmount != null ? invoiceData.totalAmount.toLocaleString() : ""}</Text>
            <Text style={{ ...styles.cell, flex: 2, borderRight: "none" }}></Text>
          </View>
        </View>

        <View style={styles.annai}>
          <Text style={{ fontSize: 15, textAlign: "center" }}>ご　案　内</Text>
          <Text style={{ marginTop: 5, marginBottom: 5 }}>{invoiceData.guide}</Text>
        </View>
      </Page>
    </Document>

  )
};

export default InvoicePDF;
